/**
 * ConfirmTaskButton — a supervisor/admin confirms a PM-requested (pending) task
 * into committed work (Calendar Phase 4b). Shared by the Home approval card and
 * the project workspace's task list.
 *
 * The dialog shows the requester's note and the requested time, and lets the
 * supervisor accept that time or commit a different time/day before confirming
 * (POST /tasks/:id/confirm with an optional schedule override). Either way the
 * requesting PM is notified — no bounce-back for a different date.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { CrewRoleCounts, Task } from '@vari-crew/schemas';
import { useConfirmTask, useTasks } from '@/api/tasks';
import { useProject } from '@/api/projects';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input, Label } from '@/components/ui/Input';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { dayTimeToIso, isoToDay, isoToTime } from '@/lib/calendarGrid';
import { formatDateOnly } from '@/lib/format';
import { formatRoleDemand } from '@/lib/crewRoles';

export interface ConfirmTaskTarget {
  id: string;
  title: string;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  request_note?: string | null;
  /** Crew headcount this task needs — shown as scheduling context when known. */
  crew_size_needed?: number | null;
  /** Planned per-role crew mix (from the studio) — shown as the demand breakdown. */
  crew_roles_needed?: CrewRoleCounts | null;
  /** When set, the dialog shows a "View project details" link AND fetches the
   *  project to surface key scheduling context (site, contact, deadline…). */
  projectId?: string | null;
}

// Dot colour per task status (mirrors the calendar/agenda status palette).
const TASK_STATUS_DOT: Record<string, string> = {
  scheduled: 'var(--color-status-scheduled-fg)',
  en_route: 'var(--color-status-enroute-fg)',
  on_site: 'var(--color-status-onsite-fg)',
  in_progress: 'var(--color-status-onsite-fg)',
  paused: 'var(--color-status-pendingcloseout-fg)',
  complete: 'var(--color-status-complete-fg)',
  blocked: 'var(--color-status-blocked-fg)',
  cancelled: 'var(--color-ink-faint)',
};

function taskStatusDot(t: Task): string {
  if (t.pending_confirmation) return 'var(--color-status-requested-fg)';
  return TASK_STATUS_DOT[t.status] ?? 'var(--color-ink-faint)';
}
function taskStatusLabel(t: Task): string {
  if (t.pending_confirmation) return 'Pending';
  return t.status.charAt(0).toUpperCase() + t.status.slice(1).replace(/_/g, ' ');
}

export function ConfirmTaskButton({
  task,
  size = 'sm',
  variant = 'default',
}: {
  task: ConfirmTaskTarget;
  size?: 'sm' | 'icon';
  variant?: 'default' | 'outline';
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Check className="w-3.5 h-3.5" />
        Confirm
      </Button>
      {open && <ConfirmTaskDialog task={task} onClose={() => setOpen(false)} />}
    </>
  );
}

export function ConfirmTaskDialog({
  task,
  onClose,
  onConfirmed,
}: {
  task: ConfirmTaskTarget;
  onClose: () => void;
  /** Fired after a successful confirm (before onClose) — e.g. so a drag-initiated
   *  open knows the move was approved and shouldn't be reverted. */
  onConfirmed?: () => void;
}) {
  const confirm = useConfirmTask(task.id);
  // Pull the parent project so the supervisor sees the context they need to
  // schedule (site, contact, access notes, deadline) without leaving the dialog.
  const projectQ = useProject(task.projectId ?? undefined);
  const project = projectQ.data;
  // …and the project's other tasks, so the project status (a rollup) is read in
  // context — e.g. "Scheduled" can mean a sibling is scheduled while THIS task
  // is still pending.
  const tasksQ = useTasks(
    { project_id: task.projectId ?? undefined, limit: 50, offset: 0 },
    { enabled: !!task.projectId },
  );
  const siblingTasks = tasksQ.data?.data ?? [];
  // Prefill from the requested time; the supervisor can keep or change it.
  const [date, setDate] = useState(isoToDay(task.scheduled_start ?? null) ?? '');
  const [startTime, setStartTime] = useState(isoToTime(task.scheduled_start ?? null) ?? '08:00');
  const [endTime, setEndTime] = useState(isoToTime(task.scheduled_end ?? null) ?? '17:00');

  const validWindow = !!date && startTime < endTime;

  async function submit() {
    if (date && !validWindow) return;
    // With a date → commit that schedule; without one → confirm unscheduled.
    const body =
      date && validWindow
        ? {
            scheduled_start: dayTimeToIso(date, startTime),
            scheduled_end: dayTimeToIso(date, endTime),
          }
        : {};
    try {
      await confirm.mutateAsync(body);
      toast.success('Task confirmed · requester notified');
      onConfirmed?.();
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <Dialog
      open
      onClose={() => !confirm.isPending && onClose()}
      busy={confirm.isPending}
      className="max-w-lg"
      title={
        <>
          <Check className="w-4 h-4" />
          Confirm requested task
        </>
      }
    >
      <div className="p-4 space-y-3">
        <div className="font-medium text-sm">{task.title}</div>

        {task.projectId && (
          <Link
            to={`/projects/${task.projectId}`}
            className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
          >
            View project details
            <ArrowUpRight className="w-3 h-3" />
          </Link>
        )}

        {/* Key context from the actual project, so the supervisor can decide the
            time/crew without opening the project screen. */}
        {task.projectId && (
          <div className="rounded-md border border-line bg-fill-soft/60 p-3 text-xs">
            {projectQ.isLoading ? (
              <div className="flex items-center gap-2 text-ink-faint">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading project…
              </div>
            ) : project ? (
              <dl className="grid grid-cols-[96px_1fr] gap-x-3 gap-y-1.5">
                <dt className="text-ink-faint">Client</dt>
                <dd className="text-ink">{project.client_name}</dd>

                {project.site_address && (
                  <>
                    <dt className="text-ink-faint">Site</dt>
                    <dd className="text-ink">
                      {project.site_address.line1}
                      {project.site_address.line2 ? `, ${project.site_address.line2}` : ''}
                      {`, ${project.site_address.city}, ${project.site_address.state} ${project.site_address.postal_code}`}
                    </dd>
                  </>
                )}

                {(project.site_contact_name || project.site_contact_phone) && (
                  <>
                    <dt className="text-ink-faint">Contact</dt>
                    <dd className="text-ink">
                      {project.site_contact_name ?? '—'}
                      {project.site_contact_phone ? (
                        <span className="text-ink-faint"> · {project.site_contact_phone}</span>
                      ) : null}
                    </dd>
                  </>
                )}

                {project.site_notes && (
                  <>
                    <dt className="text-ink-faint">Access</dt>
                    <dd className="whitespace-pre-wrap text-ink">{project.site_notes}</dd>
                  </>
                )}

                <dt className="text-ink-faint">Requested by</dt>
                <dd className="text-ink">
                  {project.pm_display_name ?? (project.source === 'form' ? '—' : 'Internal')}
                </dd>

                <dt className="text-ink-faint">Project status</dt>
                <dd className="flex items-center gap-1.5">
                  <StatusBadge status={project.status} />
                  {project.priority !== 'normal' && (
                    <span className="capitalize text-ink-dim">· {project.priority} priority</span>
                  )}
                </dd>

                {task.crew_size_needed ? (
                  <>
                    <dt className="text-ink-faint">Crew</dt>
                    <dd className="text-ink">
                      {task.crew_size_needed} needed
                      {formatRoleDemand(task.crew_roles_needed) && (
                        <span className="text-ink-dim"> · {formatRoleDemand(task.crew_roles_needed)}</span>
                      )}
                    </dd>
                  </>
                ) : null}

                {project.deadline_date && (
                  <>
                    <dt className="text-ink-faint">Deadline</dt>
                    <dd className="text-ink">{formatDateOnly(project.deadline_date)}</dd>
                  </>
                )}
              </dl>
            ) : projectQ.isError ? (
              <div className="text-ink-faint">Couldn’t load project details.</div>
            ) : null}

            {/* The project status above is a rollup — list each task's own status
                (this one marked) so a "Scheduled" project with a pending task
                reads clearly. */}
            {project && siblingTasks.length > 0 && (
              <div className="mt-2.5 border-t border-line pt-2">
                <div className="mb-1 text-ink-faint">
                  Tasks in this project ({siblingTasks.length})
                </div>
                <ul className="space-y-1">
                  {siblingTasks.slice(0, 8).map((t) => {
                    const isThis = t.id === task.id;
                    return (
                      <li key={t.id} className="flex items-center gap-1.5">
                        <span
                          aria-hidden
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: taskStatusDot(t) }}
                        />
                        <span
                          className={`truncate ${isThis ? 'font-medium text-ink' : 'text-ink-dim'}`}
                        >
                          {t.title}
                        </span>
                        <span className="ml-auto shrink-0 text-ink-faint">{taskStatusLabel(t)}</span>
                        {isThis && <span className="shrink-0 font-medium text-accent">· this one</span>}
                      </li>
                    );
                  })}
                  {siblingTasks.length > 8 && (
                    <li className="text-ink-faint">+{siblingTasks.length - 8} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {task.request_note && (
          <div className="rounded-md border border-line bg-fill-soft p-2 text-xs">
            <div className="text-ink-faint mb-0.5">Requester’s note</div>
            <div className="whitespace-pre-wrap text-ink">{task.request_note}</div>
          </div>
        )}

        <p className="text-[11px] text-ink-faint">
          Accept the requested time, or set a different time/day. The requester is notified either
          way.
        </p>

        {/* Date gets a wider column so the native date input's calendar icon
            isn't clipped (the date string is longer than the time values). */}
        <div className="grid grid-cols-[1.3fr_1fr_1fr] gap-2">
          <div>
            <Label htmlFor="cf-date">Date</Label>
            <Input id="cf-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cf-start">Start</Label>
            <Input
              id="cf-start"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="cf-end">End</Label>
            <Input
              id="cf-end"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>
        {date && !validWindow && (
          <p className="text-[11px] text-amber-700">End time must be after the start time.</p>
        )}
        {!date && (
          <p className="text-[11px] text-amber-700">
            No time requested — set one now, or confirm and schedule it later.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={confirm.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={confirm.isPending || (!!date && !validWindow)}>
            {confirm.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Confirm &amp; schedule
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
